import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material'
import { billingApi, userService, institutionService } from '../../services/api'
import { Contract, ContractCreate, ContractUpdate, ContractCloneRequest, UserWithProfile, Institution } from '../../types/api'
import { useAuth } from '../../contexts/AuthContext'
import ContractSegmentPricingComponent from '../../components/Billing/ContractSegmentPricing'
import toast from 'react-hot-toast'
import apiClient from '../../services/api'

const MediciContractsTab: React.FC = () => {
  const { user } = useAuth()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [totalContracts, setTotalContracts] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  
  // Filters
  const [medicFilter, setMedicFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('')
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  
  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false)
  const [contractToClone, setContractToClone] = useState<Contract | null>(null)
  const [cloneFormData, setCloneFormData] = useState<ContractCloneRequest>({
    new_doctor_id: '',
    valid_from: '',
    valid_to: '',
    notes: ''
  })
  
  // Form data
  const [formData, setFormData] = useState<ContractCreate>({
    doctor_id: '',
    institution_id: '',
    contract_type: 'per_segment',
    currency: 'RON',
    valid_from: new Date().toISOString().split('T')[0],
    is_active: true
  })
  
  // Dropdown data
  const [medics, setMedics] = useState<UserWithProfile[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Load contracts
  const loadContracts = async () => {
    try {
      setLoading(true)
      const response = await billingApi.getContracts({
        doctor_id: medicFilter || undefined,
        institution_id: institutionFilter || undefined,
        is_active: activeFilter !== '' ? activeFilter === 'true' : undefined,
        page: page + 1,
        size: rowsPerPage
      })
      
      setContracts(response.contracts)
      setTotalContracts(response.total)
    } catch (error) {
      console.error('Error loading contracts:', error)
      toast.error('Eroare la încărcarea contractelor')
    } finally {
      setLoading(false)
    }
  }

  // Load dropdown data
  const loadDropdownData = async () => {
    try {
      const [medicsData, institutionsData] = await Promise.all([
        userService.getUsers({ role: 'medic', limit: 200 }),
        institutionService.getInstitutions({ limit: 200 })
      ])
      
      setMedics(medicsData.users)
      setInstitutions(institutionsData.institutions)
    } catch (error) {
      console.error('Error loading dropdown data:', error)
      toast.error('Eroare la încărcarea datelor')
    }
  }

  useEffect(() => {
    loadContracts()
  }, [page, rowsPerPage, medicFilter, institutionFilter, activeFilter])

  useEffect(() => {
    loadDropdownData()
  }, [])

  const handleCreateContract = () => {
    setSelectedContract(null)
    setFormData({
      doctor_id: '',
      institution_id: '',
      contract_type: 'per_segment',
      currency: 'RON',
      valid_from: new Date().toISOString().split('T')[0],
      is_active: true
    })
    setDialogOpen(true)
  }

  const handleEditContract = (contract: Contract) => {
    setSelectedContract(contract)
    setFormData({
      doctor_id: contract.doctor_id,
      institution_id: contract.institution_id,
      contract_type: contract.contract_type,
      hourly_rate: contract.hourly_rate,
      currency: contract.currency,
      valid_from: contract.valid_from,
      valid_to: contract.valid_to,
      is_active: contract.is_active,
      notes: contract.notes
    })
    setDialogOpen(true)
  }

  const handleSaveContract = async () => {
    try {
      setDialogLoading(true)
      
      // Clean up form data based on contract type
      const cleanFormData = { ...formData }
      if (cleanFormData.contract_type === 'per_segment') {
        // Remove hourly_rate for per_segment contracts
        delete (cleanFormData as any).hourly_rate
      } else if (cleanFormData.contract_type === 'per_hour' && !cleanFormData.hourly_rate) {
        toast.error('Tariful orar este obligatoriu pentru contractele per oră')
        return
      }
      
      if (selectedContract) {
        // Update existing contract
        await billingApi.updateContract(selectedContract.id, cleanFormData as ContractUpdate)
        toast.success('Contract actualizat cu succes')
      } else {
        // Create new contract
        await billingApi.createContract(cleanFormData)
        toast.success('Contract creat cu succes')
      }
      
      setDialogOpen(false)
      loadContracts()
    } catch (error: any) {
      console.error('Error saving contract:', error)
      toast.error(error.response?.data?.detail || 'Eroare la salvarea contractului')
    } finally {
      setDialogLoading(false)
    }
  }

  const handleDeleteContract = async (contractId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest contract?')) return
    
    try {
      await billingApi.deleteContract(contractId)
      toast.success('Contract șters cu succes')
      loadContracts()
    } catch (error: any) {
      console.error('Error deleting contract:', error)
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea contractului')
    }
  }

  const handleCloneContract = (contract: Contract) => {
    setContractToClone(contract)
    setCloneFormData({
      new_doctor_id: '',
      valid_from: contract.valid_from,
      valid_to: contract.valid_to || '',
      notes: `Clonat din contractul #${contract.id.substring(0, 8)}`
    })
    setCloneDialogOpen(true)
  }

  const handleSaveClonedContract = async () => {
    if (!contractToClone) return
    
    if (!cloneFormData.new_doctor_id) {
      toast.error('Selectați medicul către care doriți să clonați contractul')
      return
    }
    
    try {
      setDialogLoading(true)
      
      // Pregătește datele pentru clonare - trimite doar câmpurile setate
      const cloneData: ContractCloneRequest = {
        new_doctor_id: cloneFormData.new_doctor_id
      }
      
      if (cloneFormData.valid_from) {
        cloneData.valid_from = cloneFormData.valid_from
      }
      if (cloneFormData.valid_to) {
        cloneData.valid_to = cloneFormData.valid_to
      }
      if (cloneFormData.notes) {
        cloneData.notes = cloneFormData.notes
      }
      
      const result = await billingApi.cloneContract(contractToClone.id, cloneData)
      
      toast.success(`${result.message} (${result.segments_cloned} segmente copiate)`)
      setCloneDialogOpen(false)
      loadContracts()
    } catch (error: any) {
      console.error('Error cloning contract:', error)
      toast.error(error.response?.data?.detail || 'Eroare la clonarea contractului')
    } finally {
      setDialogLoading(false)
    }
  }

  const handleDownloadPdf = async (contract: Contract) => {
    try {
      toast.loading('Se generează PDF-ul...', { id: 'pdf-loading' })
      
      const response = await apiClient.get(`/billing/contracts/${contract.id}/pdf`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const filename = `contract_${contract.doctor_name?.replace(/\s+/g, '_') || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success('PDF descărcat cu succes', { id: 'pdf-loading' })
    } catch (error: any) {
      console.error('Error downloading PDF:', error)
      toast.error(error.response?.data?.detail || 'Eroare la generarea PDF-ului', { id: 'pdf-loading' })
    }
  }

  const getContractTypeLabel = (type: string) => {
    switch (type) {
      case 'per_hour': return 'Pe oră'
      case 'per_segment': return 'Pe segment'
      default: return type
    }
  }

  const getContractTypeColor = (type: string) => {
    switch (type) {
      case 'per_hour': return 'primary'
      case 'per_segment': return 'secondary'
      default: return 'default'
    }
  }

  if (!user || !['admin', 'operator'].includes(user.role)) {
    return (
      <Alert severity="error">
        Nu aveți permisiuni pentru a accesa această pagină.
      </Alert>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Filters and Actions - identical to Worklist */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Medic</InputLabel>
              <Select
                value={medicFilter}
                onChange={(e) => setMedicFilter(e.target.value)}
                label="Medic"
              >
                <MenuItem value="">Toți medicii</MenuItem>
                {medics.map((medic) => (
                  <MenuItem key={medic.id} value={medic.id}>
                    {medic.doctor_profile?.first_name} {medic.doctor_profile?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Instituție</InputLabel>
              <Select
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                label="Instituție"
              >
                <MenuItem value="">Toate instituțiile</MenuItem>
                {institutions.map((institution) => (
                  <MenuItem key={institution.id} value={institution.id}>
                    {institution.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as any)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={loadContracts}
              disabled={loading}
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              Reîncarcă
            </Button>
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleCreateContract}
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              Contract Nou
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Contracts table - compact styling */}
      <Paper sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.75rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': {
                  color: 'common.white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  py: 1,
                  px: 1.5,
                  whiteSpace: 'nowrap'
                }
              }}>
                <TableCell>Medic</TableCell>
                <TableCell>Instituție</TableCell>
                <TableCell>Tip Contract</TableCell>
                <TableCell>Tarif</TableCell>
                <TableCell>Valabilitate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {contract.doctor_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contract.institution_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getContractTypeLabel(contract.contract_type)}
                      color={getContractTypeColor(contract.contract_type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {contract.contract_type === 'per_hour' ? (
                      <Typography variant="body2">
                        {contract.hourly_rate} {contract.currency}/oră
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Pe segment
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(contract.valid_from).toLocaleDateString()}
                      {contract.valid_to && (
                        <> - {new Date(contract.valid_to).toLocaleDateString()}</>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contract.is_active ? 'Activ' : 'Inactiv'}
                      color={contract.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditContract(contract)}
                      title="Editează contract"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleCloneContract(contract)}
                      color="primary"
                      title="Clonează contract"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                    {contract.contract_type === 'per_segment' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadPdf(contract)}
                        color="secondary"
                        title="Descarcă PDF"
                      >
                        <PdfIcon />
                      </IconButton>
                    )}
                    {user?.role === 'admin' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteContract(contract.id)}
                        color="error"
                        title="Șterge contract"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalContracts}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage="Contracte per pagină"
        />
      </Paper>

      {/* Contract Dialog - identical to QuickAddPatientForm */}
      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setDialogOpen(false) }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: '0.85rem',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiSelect-select': {
              color: 'text.primary',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
            },
          }
        }}
      >
        <DialogTitle sx={{ 
          py: 1.5, 
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}>
          {selectedContract ? 'Editează Contract Medic' : 'Contract Nou Medic'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Medic *</InputLabel>
                <Select
                  value={formData.doctor_id}
                  onChange={(e) => setFormData({ ...formData, doctor_id: e.target.value })}
                  label="Medic *"
                  disabled={!!selectedContract}
                >
                  {medics.map((medic) => (
                    <MenuItem key={medic.id} value={medic.id}>
                      {medic.doctor_profile?.first_name} {medic.doctor_profile?.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Instituție *</InputLabel>
                <Select
                  value={formData.institution_id}
                  onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
                  label="Instituție *"
                  disabled={!!selectedContract}
                >
                  {institutions.map((institution) => (
                    <MenuItem key={institution.id} value={institution.id}>
                      {institution.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tip Contract *</InputLabel>
                <Select
                  value={formData.contract_type}
                  onChange={(e) => setFormData({ ...formData, contract_type: e.target.value as any })}
                  label="Tip Contract *"
                >
                  <MenuItem value="per_segment">Pe Segment</MenuItem>
                  <MenuItem value="per_hour">Pe Oră</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {formData.contract_type === 'per_hour' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tarif Orar *"
                  type="number"
                  value={formData.hourly_rate || ''}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || undefined })}
                  InputProps={{
                    endAdornment: <Typography sx={{ fontSize: '0.8rem' }}>{formData.currency}/oră</Typography>
                  }}
                />
              </Grid>
            )}
            
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Data început *"
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Data sfârșit"
                type="date"
                value={formData.valid_to || ''}
                onChange={(e) => setFormData({ ...formData, valid_to: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Note"
                multiline
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            
            </Grid>

          {/* Segment Pricing Section - only for per_segment contracts */}
          {formData.contract_type === 'per_segment' && (
            <>
              {selectedContract ? (
                <ContractSegmentPricingComponent
                  contractId={selectedContract.id}
                  currency={formData.currency || 'RON'}
                />
              ) : (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 1.5, 
                    py: 0.5,
                    bgcolor: 'info.dark',
                    color: 'info.contrastText',
                    '& .MuiAlert-icon': { color: 'info.contrastText' }
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
                    <strong>Prețuri per Segment:</strong> După creare, veți putea configura prețurile per body part.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between'
        }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontSize: '0.78rem' }}>Activ</Typography>}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => setDialogOpen(false)} 
              disabled={dialogLoading}
              size="small" 
              sx={{ fontSize: '0.8rem' }}
            >
              Anulează
            </Button>
            <Button
              onClick={handleSaveContract}
              variant="contained"
              size="small"
              disabled={dialogLoading || !formData.doctor_id || !formData.institution_id}
              sx={{ fontSize: '0.8rem' }}
            >
              {selectedContract ? 'Actualizează' : 'Creează'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Clone Contract Dialog - identical to QuickAddPatientForm */}
      <Dialog
        open={cloneDialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setCloneDialogOpen(false) }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: '0.85rem',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiSelect-select': {
              color: 'text.primary',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
            },
          }
        }}
      >
        <DialogTitle sx={{ 
          py: 1.5, 
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}>
          Clonează Contract
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {contractToClone && (
            <>
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 1.5, 
                  py: 0.5,
                  bgcolor: 'info.dark',
                  color: 'info.contrastText',
                  '& .MuiAlert-icon': { color: 'info.contrastText' }
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
                  <strong>Original:</strong> {contractToClone.doctor_name} - {contractToClone.institution_name} ({getContractTypeLabel(contractToClone.contract_type)})
                </Typography>
              </Alert>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Medic Destinație *</InputLabel>
                    <Select
                      value={cloneFormData.new_doctor_id}
                      onChange={(e) => setCloneFormData({ ...cloneFormData, new_doctor_id: e.target.value })}
                      label="Medic Destinație *"
                    >
                      {medics
                        .filter(m => m.id !== contractToClone.doctor_id)
                        .map((medic) => (
                          <MenuItem key={medic.id} value={medic.id}>
                            {medic.doctor_profile?.first_name} {medic.doctor_profile?.last_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Data început"
                    type="date"
                    value={cloneFormData.valid_from}
                    onChange={(e) => setCloneFormData({ ...cloneFormData, valid_from: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Data sfârșit"
                    type="date"
                    value={cloneFormData.valid_to}
                    onChange={(e) => setCloneFormData({ ...cloneFormData, valid_to: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Note"
                    multiline
                    rows={2}
                    value={cloneFormData.notes}
                    onChange={(e) => setCloneFormData({ ...cloneFormData, notes: e.target.value })}
                  />
                </Grid>

                {contractToClone.contract_type === 'per_segment' && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        py: 0.5,
                        bgcolor: 'success.dark',
                        color: 'success.contrastText',
                        '& .MuiAlert-icon': { color: 'success.contrastText' }
                      }}
                    >
                      <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
                        ✅ Prețurile per segment vor fi copiate automat!
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}>
          <Button 
            onClick={() => setCloneDialogOpen(false)} 
            disabled={dialogLoading}
            size="small" 
            sx={{ 
              fontSize: '0.8rem',
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary'
              }
            }}
          >
            Anulează
          </Button>
          <Button
            onClick={handleSaveClonedContract}
            variant="contained"
            size="small"
            disabled={dialogLoading || !cloneFormData.new_doctor_id}
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: '0.8rem' }}
          >
            {dialogLoading ? 'Se clonează...' : 'Clonează'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Export direct MediciContractsTab as ContractsPage (no more subtabs)
const ContractsPage: React.FC = () => {
  return <MediciContractsTab />
}

export default ContractsPage
